import type { CreatureLook, EntityState, NpcLook } from "odyssey-shared";
import type { MutablePoint } from "./projection.ts";

/**
 * The characters the scene draws, as world-attached UI reads them (P1.9-T16): the local character's feet as
 * drawn this frame and its handle, and every player character and creature drawn this frame with its handle, its
 * drawn feet, how far its art reaches above them and its state as the client's view holds it, which carries its name
 * and its flag byte, or a creature's appearance and level; the player's target; and a creature's look in the world's
 * look table (R-0089). A surface such as the nameplates reads them through its context, so the scene names no
 * surface, and visiting them allocates nothing.
 */

/**
 * The default head height above a character's feet, in world pixels, 35, which stands a plate clear of the tallest
 * player art. A player drawn as a composed look uses the look layout's one head height in its place (ADR-094 decision 34), and a
 * creature's is measured from its own sheet (`creature-head.ts`); this is what stands in where neither is known.
 */
export const HEAD_ABOVE_FEET = 35;

/**
 * Told one drawn character.
 *
 * @param handle Its entity handle.
 * @param x Its feet across, in world pixels, where it is drawn this frame.
 * @param y Its feet down, in world pixels.
 * @param state Its state as the client's view holds it.
 * @param head How far its art reaches above its feet, in world pixels: the look layout's head height for a player, raised
 *   by a hop's lift while its `jump` strip plays (ADR-116's third amendment).
 * @param hidden Whether the world UI drawn for it hides (ADR-061's amendment for P1.9-T73): a remote entity concealed
 *   from the viewer, behind a prop or under a roof the viewer is not under, and not the viewer's target. Never for the
 *   local character.
 */
export type CharacterVisitor = (handle: number, x: number, y: number, state: EntityState, head: number, hidden: boolean) => void;

/**
 * The first of the numbers the world UI keys the other maps' figures by (ADR-147 decisions 7 and 13): past every handle
 * a snapshot carries (0 to 65,535), so a figure of a neighbour's edge view, or one a crossing holds, never takes the plate
 * of an entity of the owning map's stream, and nothing that reads a handle as the owning map's (the target, the player
 * menu, the emoji and bubbles of a handle) ever reaches it.
 */
export const BESIDE_HANDLE_BASE = 65_536;

/**
 * How many figures of the other maps the world UI keys at once (`BESIDE_HANDLE_BASE` on): a full edge view on each of
 * four sides and a full crossing hold.
 */
export const BESIDE_HANDLES = 320;

/**
 * The characters drawn this frame.
 */
export interface CharacterSource {
  /**
   * Where the local character's feet are drawn this frame.
   *
   * @param out Where to write them, in world pixels.
   * @returns False when no local character is drawn, before the welcome or after a session ends.
   */
  local(out: MutablePoint): boolean;
  /**
   * The local character's entity handle, which `forEach` visits it under, so a surface can tell the player's own
   * character from the others, as the nameplates do (P1.9-T24).
   *
   * @returns The handle, or -1 when no local character is drawn.
   */
  localHandle(): number;
  /**
   * Visits every player character and creature drawn this frame whose state the view holds, the local one included,
   * each with whether the world UI drawn for it hides; a surface keeps what it holds for a hidden one and hides it, so
   * it shows again the frame the entity does.
   *
   * @param visit Told each character.
   */
  forEach(visit: CharacterVisitor): void;
  /**
   * The player's target (P2.2-T3), whose creature shows its health bar at full health (R-0089).
   *
   * @returns Its handle, or -1 for none.
   */
  target(): number;
  /**
   * A creature's look in the world's look table: its name, temperament and rank, which its nameplate draws.
   *
   * @param appearance The creature's Appearance component.
   * @returns The look, or null past the table's end.
   */
  creatureLook(appearance: number): CreatureLook | null;
  /**
   * An NPC's look in the world's NPC look table (ADR-136 decision 2.4): its name and plate colour, which its nameplate
   * draws.
   *
   * @param appearance The NPC's Appearance component.
   * @returns The look, or null past the table's end.
   */
  npcLook(appearance: number): NpcLook | null;
}

/**
 * The source of a scene that draws no characters, as the `/ui` gallery's pages.
 */
export const NO_CHARACTERS: CharacterSource = Object.freeze({
  /**
   * Draws no local character.
   *
   * @returns False.
   */
  local: (): boolean => false,
  /**
   * Names no local character.
   *
   * @returns -1.
   */
  localHandle: (): number => -1,
  /**
   * Visits nothing.
   */
  forEach: (): void => undefined,
  /**
   * Targets nothing.
   *
   * @returns -1.
   */
  target: (): number => -1,
  /**
   * Holds no look table.
   *
   * @returns Null.
   */
  creatureLook: (): CreatureLook | null => null,
  /**
   * Holds no NPC look table.
   *
   * @returns Null.
   */
  npcLook: (): NpcLook | null => null,
});
